import { useState } from 'react';
import type { Review } from '@salonomy/types';
import { Button, Field, Modal, TextArea } from '@salonomy/ui';
import { useModerateReview, useReplyToReview } from '../../data/reviews.js';
import { useToast } from '../../lib/toast.js';
import './reviews.css';

type Mode = 'reply' | 'hide' | 'publish';

/**
 * The moderation action sheet for a single review. Three intents, all manager+ on the API:
 *  - 'reply'   → POST /reviews/{id}/reply (attach/replace the public salon reply; reply is required)
 *  - 'hide'    → PATCH /reviews/{id} { status: 'hidden' } (suppress a published/pending review)
 *  - 'publish' → PATCH /reviews/{id} { status: 'published' } (approve a pending one / un-hide)
 *
 * Publish/hide may carry an OPTIONAL reply in the same PATCH; the dedicated reply intent requires one.
 * We never compute or persist anything here — the server is the source of truth for status + aggregate,
 * and the hooks invalidate the whole reviews tree so the header re-reads after the write.
 */
export function ReviewModerationModal({
  review,
  mode,
  onClose,
}: {
  review: Review;
  mode: Mode;
  onClose: () => void;
}) {
  const moderate = useModerateReview();
  const reply = useReplyToReview();
  const toast = useToast();

  const [text, setText] = useState(review.salonReply ?? '');
  const [busy, setBusy] = useState(false);

  const title =
    mode === 'reply'
      ? review.salonReply
        ? 'Edit reply'
        : 'Reply publicly'
      : mode === 'hide'
        ? 'Hide review'
        : review.status === 'hidden'
          ? 'Publish review'
          : 'Publish review';

  async function submit() {
    const trimmed = text.trim();
    if (mode === 'reply' && !trimmed) {
      toast.error('A reply can’t be empty.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'reply') {
        await reply.mutateAsync({ reviewId: review.id, body: { salonReply: trimmed } });
        toast.success('Reply published');
      } else {
        await moderate.mutateAsync({
          reviewId: review.id,
          body: {
            status: mode === 'hide' ? 'hidden' : 'published',
            ...(trimmed ? { salonReply: trimmed } : {}),
          },
        });
        toast.success(mode === 'hide' ? 'Review hidden' : 'Review published');
      }
      onClose();
    } catch {
      toast.error('Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} variant={mode === 'hide' ? 'danger' : 'primary'}>
            {mode === 'reply' ? 'Save reply' : mode === 'hide' ? 'Hide' : 'Publish'}
          </Button>
        </>
      }
    >
      <div className="muted" style={{ fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
        {mode === 'hide' ? (
          <>
            Hiding removes this review from your public booking page and from your star rating. It
            is kept here so you can publish it again later.
          </>
        ) : mode === 'publish' ? (
          <>
            Publishing makes this review live on your public booking page and counts it towards your
            star rating.
          </>
        ) : (
          <>Your reply shows publicly beneath the review. Keep it brief and professional.</>
        )}
      </div>

      <Field
        label={mode === 'reply' ? 'Public reply' : 'Public reply (optional)'}
        hint="This text is visible to anyone viewing your booking page."
      >
        <TextArea
          autoFocus
          rows={4}
          value={text}
          maxLength={2000}
          onChange={(e) => setText(e.target.value)}
          placeholder="Thanks so much for the kind words…"
        />
      </Field>
    </Modal>
  );
}
